import { BottomSheet, cn } from "@breenova/ui";
import {
  ArrowLeft,
  Bird,
  CalendarDays,
  Camera,
  ChevronRight,
  Egg,
  HeartPulse,
  Plus,
  RefreshCw,
  Scale,
  Settings,
  Syringe,
  Sunrise,
  UserRound,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";

import { useAuth } from "@/auth/auth-context";
import { useBreedingCalendar } from "@/lib/breeding";
import { useOverview } from "@/lib/stats";
import { applyUpdate, dismissUpdate, useAppUpdate } from "@/lib/app-update";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

/**
 * The four domains the floating pill exposes.
 *
 * Settings are deliberately not among them: they moved to the avatar in each screen's
 * own header, which freed the centre slot for logging — the thing a breeder actually
 * does standing in front of a cage.
 */
const NAV_ITEMS: readonly NavItem[] = [
  { to: "/", label: "Today", icon: Sunrise },
  { to: "/birds", label: "Birds", icon: Bird },
  { to: "/breeding", label: "Nests", icon: Egg },
  { to: "/health", label: "Care", icon: HeartPulse },
];

/** The phone's tab bar: the four domains plus "Me", which holds settings. */
const TAB_ITEMS: readonly NavItem[] = [
  ...NAV_ITEMS,
  { to: "/more", label: "Me", icon: UserRound },
];

/**
 * The rail carries a calendar row too.
 *
 * The calendar is a screen you read at a desk with a month in front of you, not one you
 * open one-handed in front of a cage — so it earns a rail row at the width where a month
 * fits, and stays off the phone's tab bar, which is already full.
 */
const RAIL_NAV_ITEMS: readonly NavItem[] = [
  ...NAV_ITEMS,
  { to: "/breeding/calendar", label: "Calendar", icon: CalendarDays },
];

/**
 * The two places a screen may write into the desktop rail.
 *
 * Portals rather than props threaded through `Outlet` context: the rail is rendered by
 * the shell and the content belongs to the route, and a portal is the one arrangement
 * where the route keeps ownership of its own state. Birds' filter pills are the reason —
 * they are the list's filter state, and passing them up to the shell would put a screen's
 * state one level above the screen.
 */
interface RailSlots {
  action: HTMLElement | null;
  section: HTMLElement | null;
  /** Called by {@link RailPrimaryAction} on mount and unmount, so the rail knows
      whether to draw its own default button or stand aside for the screen's. */
  claimAction: (claimed: boolean) => void;
}

const RailSlotContext = createContext<RailSlots>({
  action: null,
  section: null,
  claimAction: () => {},
});

/**
 * The rail's primary action — "Add a bird" on the register, "Log a record" on Today.
 *
 * A screen that names no action gets the quick-log button, because the rail's action
 * slot is never empty: an outline where a button was on the last screen reads as
 * something that failed to render.
 */
export function RailPrimaryAction({ children }: { children: ReactNode }) {
  const { action, claimAction } = useContext(RailSlotContext);

  useEffect(() => {
    claimAction(true);

    return () => claimAction(false);
  }, [claimAction]);

  return action ? createPortal(children, action) : null;
}

/** A screen's own block in the rail: cages on Today, filters on the register. */
export function RailSection({ children }: { children: ReactNode }) {
  const { section } = useContext(RailSlotContext);

  return section ? createPortal(children, section) : null;
}

/** One labelled group inside {@link RailSection}. */
export function RailGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="px-4 text-rail-label uppercase text-on-surface-variant">{label}</span>
      {children}
    </div>
  );
}

/**
 * A row in a rail group: a label, and a count or a status on the right.
 *
 * Shorter than a nav row (38px against 44px) because the whole group is secondary to the
 * five sections above it — and because these are not the app's navigation, they are one
 * screen's filters. The 44px floor is a touch target; this row is only ever moused.
 */
export function RailRow({
  to,
  onClick,
  selected = false,
  leading,
  label,
  trailing,
  tone = "default",
}: {
  to?: string | undefined;
  onClick?: (() => void) | undefined;
  selected?: boolean | undefined;
  leading?: ReactNode;
  label: string;
  trailing?: ReactNode;
  /** `alert` puts the trailing figure in the error colour — "3 late", not "2 nests". */
  tone?: "default" | "alert" | undefined;
}) {
  const className = cn(
    "flex h-pill items-center gap-2.5 rounded-control px-4 text-left",
    "text-cell font-semibold transition-colors",
    selected
      ? "bg-primary-fixed text-on-primary-fixed"
      : "bg-surface-container text-on-surface hover:bg-surface-container-high",
  );

  const body = (
    <>
      {leading}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {trailing ? (
        <span
          className={cn(
            "shrink-0",
            tone === "alert" && !selected ? "font-bold text-secondary" : "text-on-surface-variant",
          )}
          data-numeric
        >
          {trailing}
        </span>
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={className}>
        {body}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      {body}
    </button>
  );
}

/**
 * The signed-in frame: a floating navigation pill on a phone, a persistent rail from
 * `md` up.
 *
 * The design is mobile-first because that is where a breeder actually uses this —
 * standing in an aviary, one-handed. There is no fixed top bar any more: each screen
 * writes its own header, so the phone gives its full height to the record rather than to
 * chrome that repeats the app's name back at you.
 */
export function AppShell() {
  const [quickLogOpen, setQuickLogOpen] = useState(false);

  /*
   * The bird form is a wizard with its own footer, and it takes the whole screen: the tab
   * bar under a Back / Next pair is two rows of navigation fighting for one thumb.
   */
  const { pathname } = useLocation();
  const fullScreen = /^\/birds\/(new|[^/]+\/edit)$/.test(pathname);

  /*
   * State rather than refs, because a portal needs the node to exist on the render that
   * reads it. A ref is still null on the first pass, and the slot would stay empty until
   * something unrelated re-rendered the tree.
   */
  const [actionSlot, setActionSlot] = useState<HTMLElement | null>(null);
  const [sectionSlot, setSectionSlot] = useState<HTMLElement | null>(null);
  const [actionClaimed, setActionClaimed] = useState(false);

  /*
   * Stable, so the effect in `RailPrimaryAction` runs on mount and unmount rather than
   * on every render of the screen that uses it — an unstable callback there would claim
   * and release the slot on each keystroke in a filter field.
   */
  const claimAction = useCallback((claimed: boolean) => setActionClaimed(claimed), []);

  const slots = useMemo<RailSlots>(
    () => ({ action: actionSlot, section: sectionSlot, claimAction }),
    [actionSlot, sectionSlot, claimAction],
  );

  return (
    <RailSlotContext.Provider value={slots}>
      <div className="min-h-dvh bg-surface">
        <Sidebar
          onQuickLog={() => setQuickLogOpen(true)}
          actionRef={setActionSlot}
          sectionRef={setSectionSlot}
          actionClaimed={actionClaimed}
        />

        {/* Padding utilities read the spacing scale only, so the rail's width — a
            container token, because it is a width — comes through by name. */}
        <div className="md:pl-[var(--container-sidebar)]">
          {/* Bottom padding clears the floating pill, the inset it floats on, and the
              home indicator beneath both. */}
          <main
            className={cn(
              /*
               * No cap. The artboards give the main column whatever the rail leaves —
               * a 1200px ceiling reads as a centred document with margins either side,
               * which is the one thing a desktop tool should not look like.
               */
              "w-full",
            /*
             * Installed on an iPhone the app draws under the status bar — that is what
             * `apple-mobile-web-app-status-bar-style: black-translucent` asks for, and
             * what makes it look like an app rather than a page with a white strip on
             * top. The clock has to land on the page ground rather than on a screen's
             * title, so the inset is paid here once, above every header.
             *
             * In a browser tab `env(safe-area-inset-top)` is zero and this is a no-op.
             */
              "pt-[var(--safe-area-top)]",
              /* Clears the tab bar, the home indicator, and the floating log button above it. */
              fullScreen
                ? "pb-[var(--safe-area-bottom)]"
                : "pb-[calc(var(--spacing-navbar)+var(--nav-bottom-gap)+var(--safe-area-bottom)+var(--spacing-quick-log)+var(--spacing-lg))]",
              "md:pb-xl",
            )}
          >
            <Outlet />
          </main>
        </div>

        {fullScreen ? null : <BottomNav onQuickLog={() => setQuickLogOpen(true)} />}

        <UpdateBanner />

        {quickLogOpen ? <QuickLogSheet onClose={() => setQuickLogOpen(false)} /> : null}
      </div>
    </RailSlotContext.Provider>
  );
}

interface RailCount {
  value: number;
  /**
   * `plain` is a figure — how many birds, how many nests. The other two are badges,
   * and the difference is that a badge is a thing to deal with rather than a size.
   * `alarm` is solid red and reserved for what is past its window; `warning` is the
   * tinted red a live withdrawal gets, which is a constraint rather than a failure.
   */
  tone: "plain" | "alarm" | "warning";
}

function RailCountBadge({ count, active }: { count: RailCount | undefined; active: boolean }) {
  if (!count) {
    return null;
  }

  if (count.tone === "plain") {
    return (
      <span
        className={cn(
          "flex h-5.5 min-w-5.5 shrink-0 items-center justify-center px-1.75 text-count",
          active ? "text-on-primary-fixed" : "text-on-surface-variant",
        )}
        data-numeric
      >
        {count.value}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "flex h-5.5 min-w-5.5 shrink-0 items-center justify-center rounded-control px-1.75",
        "bg-action text-count text-on-action",
      )}
      data-numeric
    >
      {count.value}
    </span>
  );
}

/**
 * The rail beside every screen from `md`: slim on a tablet, full on a desktop.
 *
 * Slim between `md` and `xl` — a 92px column of icons with their labels under them, the
 * log button at the top and the account disc at the foot. A tablet held in landscape
 * wants its width for a list and a record side by side, and a 256px rail of words is
 * what that costs.
 *
 * From `xl` it opens up: the brand, a primary action, the sections with their counts,
 * the screen's own block, and the account at the foot. A rail that only navigates wastes
 * a monitor's width on something the tab bar already does in 72px of a phone — so it
 * carries the numbers a breeder would otherwise open a screen to read, and the screen it
 * is standing on gets to add its own block beneath them.
 */
function Sidebar({
  onQuickLog,
  actionRef,
  sectionRef,
  actionClaimed,
}: {
  onQuickLog: () => void;
  actionRef: (node: HTMLElement | null) => void;
  sectionRef: (node: HTMLElement | null) => void;
  actionClaimed: boolean;
}) {
  const { user } = useAuth();
  const { data: stats } = useOverview();
  const { data: calendar } = useBreedingCalendar();

  /*
   * What is past its window, counted once for the whole rail.
   *
   * Both of these ride on queries a screen is already making — the overview and the
   * calendar — so the rail costs no request of its own on any screen that shows either,
   * and one on the screens that show neither.
   */
  const overdue = (calendar?.entries ?? []).filter((entry) => entry.isOverdue).length;

  /*
   * Counts, by section. `undefined` prints nothing rather than a zero — a rail that says
   * "Birds 0" while the overview is still in flight is worse than one that says "Birds",
   * because the zero is a fact and the blank is an absence. A badge is likewise absent at
   * zero rather than showing one: nothing overdue is not news.
   */
  const counts: Record<string, RailCount | undefined> = {
    "/": overdue > 0 ? { value: overdue, tone: "alarm" } : undefined,
    "/birds": stats ? { value: stats.birdsInAviary, tone: "plain" } : undefined,
    "/breeding": stats ? { value: stats.activeNests, tone: "plain" } : undefined,
    "/health":
      stats && stats.birdsInWithdrawal > 0
        ? { value: stats.birdsInWithdrawal, tone: "warning" }
        : undefined,
  };

  return (
    <nav
      aria-label="Sections"
      className={cn(
        "absolute inset-y-0 left-0 z-20 hidden w-sidebar flex-col items-center gap-2 py-6",
        "border-r border-outline-variant bg-surface-container-lowest",
        "md:flex",
        "xl:items-stretch xl:gap-5 xl:px-3.5 xl:pb-3.5 xl:pt-6.5",
      )}
    >
      <div className="mb-2 flex items-center gap-2.5 xl:mb-0 xl:px-1.5">
        <span
          aria-hidden
          className={cn(
            "flex size-12 shrink-0 items-center justify-center rounded-full xl:size-10.5",
            "bg-primary text-on-primary",
          )}
        >
          <Bird className="size-5.5 xl:size-5" />
        </span>

        <div className="hidden min-w-0 flex-col xl:flex">
          <span className="truncate text-brand text-on-surface">BreeNova</span>
          <span className="truncate text-rail-label uppercase text-on-surface-variant">
            {user?.displayName ? `${user.displayName.split(/\s+/)[0]}'s aviary` : "Your aviary"}
          </span>
        </div>
      </div>

      {/* The slim rail's log button: the same sheet the phone's floating button opens. */}
      <button
        type="button"
        onClick={onQuickLog}
        aria-label="Log something"
        data-quick-log=""
        className={cn(
          "mb-3 flex size-13 shrink-0 items-center justify-center rounded-full xl:hidden",
          "bg-action text-on-action shadow-raised transition-transform hover:scale-105",
        )}
      >
        <Plus className="size-6" aria-hidden />
      </button>

      {/*
        The action slot. A screen fills it through `RailPrimaryAction`; the quick-log
        button below is what shows when none does, and it is the same action the phone's
        floating button opens — so the rail and the tab bar lead to one sheet, not two.
      */}
      <div ref={actionRef} className="hidden xl:block">
        {actionClaimed ? null : (
          <button
            type="button"
            onClick={onQuickLog}
            className={cn(
              "flex h-12 w-full items-center justify-center gap-2 rounded-control",
              "bg-action text-cta-sm text-on-action",
              "transition-opacity hover:opacity-90",
            )}
          >
            <Plus className="size-4.5" aria-hidden />
            Log a record
          </button>
        )}
      </div>

      {/* The rail scrolls as one column: a screen with a long filter block should not
          push the account off the bottom, and the sections should not be pinned while
          the block below them scrolls independently. */}
      <div className="flex min-h-0 flex-1 flex-col items-center gap-5 overflow-y-auto xl:items-stretch">
        <div className="flex flex-col items-center gap-2 xl:items-stretch xl:gap-1">
          {RAIL_NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                cn(
                  "transition-colors",
                  // Slim: the icon on a small pill, the word under it.
                  "flex w-19 flex-col items-center gap-1 py-1.5 text-tab-label",
                  isActive ? "text-on-primary-fixed-variant" : "text-on-surface-variant",
                  // Full: one row, the count on the right.
                  "xl:h-11.5 xl:w-auto xl:flex-row xl:gap-3 xl:rounded-control xl:px-4 xl:py-0",
                  "xl:text-nav-item",
                  isActive
                    ? "xl:bg-primary-fixed xl:text-on-primary-fixed"
                    : "xl:text-on-surface-body xl:hover:bg-surface-container-low xl:hover:text-on-surface",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "flex h-8 w-14 items-center justify-center rounded-full",
                      "xl:h-auto xl:w-auto xl:bg-transparent",
                      isActive ? "bg-primary-fixed" : "",
                    )}
                  >
                    <item.icon className="size-5.25 shrink-0 xl:size-4.75" aria-hidden />
                  </span>
                  <span className="min-w-0 truncate xl:flex-1">{item.label}</span>
                  <span className="hidden xl:contents">
                    <RailCountBadge count={counts[item.to]} active={isActive} />
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden flex-col gap-5 xl:flex" ref={sectionRef} />
      </div>

      <Link
        to="/more"
        aria-label="Your account and settings"
        className={cn(
          "mt-auto flex items-center gap-2.5 transition-colors hover:text-on-surface",
          "xl:mt-0 xl:border-t xl:border-outline-variant xl:px-1.5 xl:pt-3",
        )}
      >
        <Avatar name={user?.displayName} className="size-11 text-avatar-sm xl:size-10" />

        <div className="hidden min-w-0 flex-1 flex-col xl:flex">
          <span className="truncate text-data text-on-surface">
            {user?.displayName ?? "Your aviary"}
          </span>
          {user?.ringCode ? (
            <span className="truncate text-caption text-on-surface-variant" data-numeric>
              {user.ringCode}
            </span>
          ) : null}
        </div>

        <Settings className="hidden size-4.5 shrink-0 text-on-surface-variant xl:block" aria-hidden />
      </Link>
    </nav>
  );
}

/**
 * The phone's navigation: a flat tab bar along the bottom edge with five tabs, and the
 * log button floating above it on the right, where a thumb rests.
 */
function BottomNav({ onQuickLog }: { onQuickLog: () => void }) {
  return (
    <>
      <button
        type="button"
        onClick={onQuickLog}
        aria-label="Log something"
        data-quick-log=""
        className={cn(
          "absolute right-5 z-20 flex size-quick-log items-center justify-center rounded-full",
          "bottom-[calc(var(--spacing-navbar)+var(--nav-bottom-gap)+var(--safe-area-bottom)+var(--spacing)*4)]",
          "bg-action text-on-action shadow-raised transition-transform hover:scale-105",
          "md:hidden",
        )}
      >
        <Plus className="size-7" aria-hidden />
      </button>

      <nav
        aria-label="Sections"
        className={cn(
          "absolute inset-x-[var(--spacing-navbar-inset)] z-20 mx-auto flex max-w-form items-center justify-around",
          "bottom-[calc(var(--nav-bottom-gap)+var(--safe-area-bottom))] h-navbar",
          "rounded-full border border-outline-variant bg-surface-container-lowest px-1.5 shadow-raised",
          "md:hidden",
        )}
      >
        {TAB_ITEMS.map((item) => (
          <NavTab key={item.to} item={item} />
        ))}
      </nav>
    </>
  );
}

/**
 * One tab. The active one sits on a full circle that holds both the icon and the label —
 * a disc, not an oval behind the icon alone.
 */
function NavTab({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) =>
        cn(
          "flex aspect-square w-[min(var(--spacing-nav-disc),20%)] shrink-0 flex-col items-center justify-center gap-hair",
          "rounded-full text-tab-label transition-colors",
          isActive
            ? "bg-primary-fixed text-on-primary-fixed-variant"
            : "text-on-surface-variant",
        )
      }
    >
      <item.icon className="size-5.25" aria-hidden />
      {item.label}
    </NavLink>
  );
}

/**
 * Says a newer deploy is on the server, with the button that moves onto it.
 *
 * At the top, under the status bar, because the bottom of the screen already holds the
 * pill and the FAB. Dismissable for the session: the update is never forced, because a
 * reload in the middle of typing a ring number would lose it.
 */
function UpdateBanner() {
  const { status, dismissed } = useAppUpdate();
  const [updating, setUpdating] = useState(false);

  if (status !== "available" || dismissed) {
    return null;
  }

  return (
    <div
      role="status"
      className={cn(
        "absolute inset-x-[var(--spacing-navbar-inset)] z-30 mx-auto max-w-form",
        "top-[calc(var(--safe-area-top)+var(--spacing-sm))]",
        "flex items-center gap-sm rounded-full py-xs pl-lg pr-xs",
        "bg-inverse-surface text-inverse-on-surface shadow-float",
        "md:left-[calc(var(--container-sidebar)+var(--spacing-lg))]",
      )}
    >
      <span className="min-w-0 flex-1 text-label">A new version of BreeNova is ready</span>

      <button
        type="button"
        disabled={updating}
        onClick={() => {
          setUpdating(true);
          void applyUpdate();
        }}
        className={cn(
          "flex min-h-touch shrink-0 items-center gap-xs rounded-full px-md text-label",
          "bg-secondary-container text-on-secondary-container",
        )}
      >
        <RefreshCw className={cn("size-4", updating && "animate-spin")} aria-hidden />
        {updating ? "Updating…" : "Update"}
      </button>

      <button
        type="button"
        onClick={dismissUpdate}
        aria-label="Later"
        className="flex size-touch shrink-0 items-center justify-center rounded-full"
      >
        <X className="size-5" aria-hidden />
      </button>
    </div>
  );
}

interface QuickLogAction {
  label: string;
  hint: string;
  icon: LucideIcon;
  to: string;
}

/**
 * The centre action's sheet.
 *
 * Every one of these ends in a mutation that needs a subject — which bird, which nest —
 * and the sheet cannot know it. So each row is a shortcut *to* the screen that asks,
 * rather than a form that would have to reproduce the picker inside the sheet. "Add a
 * bird" is the one that needs nothing and goes straight there.
 */
const QUICK_LOG_ACTIONS: readonly QuickLogAction[] = [
  { label: "Log an egg", hint: "Pick the round it belongs to", icon: Egg, to: "/breeding" },
  { label: "Weigh a bird", hint: "Pick the bird, then enter grams", icon: Scale, to: "/birds" },
  {
    label: "Record a treatment",
    hint: "One bird or a whole cage",
    icon: Syringe,
    to: "/health?new=1",
  },
  { label: "Add a photo", hint: "Pick the bird to photograph", icon: Camera, to: "/birds" },
  { label: "Add a bird", hint: "A ring number and a species is enough", icon: Bird, to: "/birds/new" },
];

function QuickLogSheet({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  return (
    <BottomSheet open onClose={onClose} title="Log something">
      <ul className="flex flex-col gap-2">
        {QUICK_LOG_ACTIONS.map((action) => (
          <li key={action.label}>
            <button
              type="button"
              onClick={() => {
                onClose();
                void navigate(action.to);
              }}
              className={cn(
                "flex min-h-touch w-full items-center gap-md rounded-inset p-3 text-left",
                "bg-surface-container-lowest transition-colors hover:bg-surface-container-low",
              )}
            >
              <span
                aria-hidden
                className="flex size-11.5 shrink-0 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed-variant"
              >
                <action.icon className="size-5.25" />
              </span>

              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-row text-on-surface">{action.label}</span>
                <span className="truncate text-caption text-on-surface-variant">
                  {action.hint}
                </span>
              </span>

              <ChevronRight className="size-5 shrink-0 text-outline" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </BottomSheet>
  );
}

/**
 * The breeder's initials on a tinted disc. There is no photo upload for a breeder yet,
 * and an empty grey circle in the identity slot reads as a failed image load.
 */
export function Avatar({
  name,
  className,
}: {
  name?: string | null | undefined;
  className?: string | undefined;
}) {
  const initials = useMemo(() => {
    const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);

    if (parts.length === 0) {
      return "BN";
    }

    return parts
      .slice(0, 2)
      .map((part) => part[0]!.toUpperCase())
      .join("");
  }, [name]);

  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full",
        "bg-primary-fixed text-avatar text-on-primary-fixed",
        className,
      )}
    >
      {initials}
    </span>
  );
}

/**
 * A section screen's header: the title with a kicker beneath it, and the avatar on the
 * right standing in for the settings tab the pill no longer has.
 *
 * Flush left rather than centred. A centred title reads as chrome; this one reads as the
 * first line of the screen, which is what it is.
 *
 * From `md` it becomes a desktop title bar: a wider gutter, more air above, and a rule
 * across the bottom. The rule is what a desktop window needs and a phone does not — on a
 * 1200px column the title and the content below it are far enough apart to read as two
 * unrelated things, and a hairline is what says they are one screen. The avatar goes at
 * the same breakpoint, because the sidebar is already showing who is signed in.
 */
export function PageHeader({
  kicker,
  title,
  subtitle,
  action,
  toolbar,
  compact = false,
}: {
  /** Sits above the title — the date on Today, nothing on a list screen. */
  kicker?: string | undefined;
  title: string;
  subtitle?: string | undefined;
  action?: ReactNode;
  /**
   * A search box, a view switch, a primary action — the controls a list screen carries.
   *
   * One node rendered in two places by CSS rather than two copies behind breakpoints:
   * a search field duplicated for mobile and desktop is two inputs with two cursors and
   * two pieces of state, and the one that is hidden is the one that keeps what was typed.
   */
  toolbar?: ReactNode;
  /**
   * Keep the phone's layout at every width: for a list drawn in a 390px column beside a
   * record, where the desktop header's single line has no room to be one.
   */
  compact?: boolean;
}) {
  // The desktop classes, or nothing when the header lives in a narrow column.
  const wide = (classes: string) => (compact ? "" : classes);

  return (
    <header
      className={cn(
        "flex flex-wrap items-start gap-4 px-5 pb-4",
        compact ? "pt-6.5" : "pt-3.5",
        /* 30px down and 36px in, which is the frame the desktop artboard draws its main
           column in. No rule: the title sits on the page ground with the screen's own
           first block under it, and a hairline across the width put a lid on it. */
        wide("md:flex-nowrap md:items-end md:gap-3.5"),
        wide("md:px-gutter-wide md:pb-0 md:pt-7.5"),
      )}
    >
      {/*
        The kicker carries the screen's counts on a desktop — "64 in the aviary · 3 cages"
        sits above the title there, where the phone puts it underneath as a subtitle. One
        node each, so neither layout is a copy of the other's text.
      */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {kicker ? (
          <span className="text-kicker uppercase text-on-surface-variant">{kicker}</span>
        ) : null}
        {subtitle ? (
          <span
            className={cn("hidden text-label-caps uppercase text-on-surface-variant", wide("md:block"))}
            data-numeric
          >
            {subtitle}
          </span>
        ) : null}
        <h1 className={cn("text-page-title text-on-surface", wide("md:text-page-title-wide"))}>
          {title}
        </h1>
        {subtitle ? (
          <p className={cn("text-detail-lg text-on-surface-variant", wide("md:hidden"))} data-numeric>
            {subtitle}
          </p>
        ) : null}
      </div>

      {/* `order-last` drops it onto its own full-width line under the title on a phone;
          from `md` it takes its DOM position and sits on the title's line, which is the
          whole difference between the two layouts. */}
      {toolbar ? (
        <div
          className={cn(
            "order-last flex w-full items-center gap-sm",
            wide("md:order-none md:w-auto md:shrink-0"),
          )}
        >
          {toolbar}
        </div>
      ) : null}

      {action ? <div className="flex shrink-0 items-center gap-sm">{action}</div> : null}
    </header>
  );
}

/**
 * A detail screen's header: a round back button with the record named beside it.
 *
 * Left-aligned and in the content flow rather than a fixed bar. The kicker carries the
 * context — which cage, which round — so the title can be the record's own name and
 * nothing else.
 */
export function DetailHeader({
  kicker,
  title,
  backTo,
  action,
  size = "default",
}: {
  kicker?: string | undefined;
  title: string;
  backTo: string;
  action?: ReactNode;
  /** `large` for a screen that names a place rather than a record — "Calendar". */
  size?: "default" | "large";
}) {
  return (
    <header
      className={cn(
        "flex items-center gap-3 px-5 pb-4 pt-3.5",
        "md:px-gutter-wide md:pb-0 md:pt-7.5",
      )}
    >
      <Link
        to={backTo}
        aria-label="Back"
        className={cn(
          "flex size-touch shrink-0 items-center justify-center rounded-full",
          "bg-surface-container-lowest text-on-surface shadow-card",
          "transition-colors hover:bg-surface-container-low",
        )}
      >
        <ArrowLeft className="size-5" />
      </Link>

      <div className="flex min-w-0 flex-1 flex-col">
        {kicker ? (
          <span className="truncate text-label-caps uppercase text-on-surface-variant" data-numeric>
            {kicker}
          </span>
        ) : null}
        <h1
          className={cn(
            "truncate text-on-surface",
            size === "large" ? "text-header-lg" : "text-section-header-wide",
          )}
          data-numeric
        >
          {title}
        </h1>
      </div>

      {action}
    </header>
  );
}
